import { CallSelect } from "./CallSelect"
import { AUTO_PICTURE_IN_PICTURE_OPTIONS, EMOJI_SKIN_TONES, useCallPreferencesContext, type AutoPictureInPicturePreference, type ChatNotificationsPreference, type EmojiSkinTone } from "./useCallPreferences"

export const CHAT_NOTIFICATION_OPTIONS: Array<{ id: ChatNotificationsPreference; label: string }> = [
  { id: "when-closed", label: "When chat is closed" },
  { id: "always", label: "Always" },
  { id: "never", label: "Never" },
]

const SKIN_TONE_LABELS: Record<EmojiSkinTone, string> = {
  "": "Use default emoji skin tone",
  "🏻": "Use light emoji skin tone",
  "🏼": "Use medium light emoji skin tone",
  "🏽": "Use medium emoji skin tone",
  "🏾": "Use medium dark emoji skin tone",
  "🏿": "Use dark emoji skin tone",
}

/** Settings > General: automatic picture-in-picture and chat notifications (Prebuilt's call-language switch is not ported). */
export function GeneralPreferencesSettings() {
  const { preferences, updatePreferences } = useCallPreferencesContext()
  return (
    <div data-testid="call-general-settings" className="space-y-4">
      <CallSelect
        id="call-auto-pip"
        label="Automatic picture-in-picture"
        value={preferences.autoPictureInPicture}
        options={AUTO_PICTURE_IN_PICTURE_OPTIONS.map((option) => ({ value: option.id, label: option.label }))}
        onChange={(value) => updatePreferences({ autoPictureInPicture: value as AutoPictureInPicturePreference })}
      />
      <p className="-mt-3 text-[11px] text-white/50">The floating call can open when you start sharing your screen. Chrome can also open it when you leave the tab if the site&apos;s Automatic picture-in-picture permission allows it.</p>
      <CallSelect
        id="call-chat-notifications"
        label="Chat notifications"
        value={preferences.chatNotifications}
        options={CHAT_NOTIFICATION_OPTIONS.map((option) => ({ value: option.id, label: option.label }))}
        onChange={(value) => updatePreferences({ chatNotifications: value as ChatNotificationsPreference })}
      />
    </div>
  )
}

/** Settings > Reactions: the default emoji skin tone applied to the hand reactions. */
export function ReactionPreferencesSettings() {
  const { preferences, updatePreferences } = useCallPreferencesContext()
  return (
    <div data-testid="call-reaction-settings">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-white/60">Default emoji skin tone</span>
      <div role="radiogroup" aria-label="Default emoji skin tone" className="flex flex-wrap gap-1.5">
        {EMOJI_SKIN_TONES.map((tone) => {
          const checked = preferences.emojiSkinTone === tone
          return (
            <button
              key={tone || "default"}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={SKIN_TONE_LABELS[tone]}
              onClick={() => updatePreferences({ emojiSkinTone: tone })}
              className={`flex h-10 w-10 items-center justify-center rounded-full text-2xl leading-none transition ${checked ? "bg-white/20 ring-2 ring-[#f6a35c]" : "bg-white/5 hover:bg-white/10"}`}
            >
              {`✋${tone}`}
            </button>
          )
        })}
      </div>
    </div>
  )
}
